import { Link, useNavigate } from "react-router-dom";
import { FiTarget, FiCheckCircle, FiLayers, FiAward, FiArrowRight } from "react-icons/fi";
import { useRole } from "../../context/RoleContext";
import { useCycle } from "../../context/CycleContext";
import { getEmployeeDashboard } from "../../services/dashboardApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, StatTile, Loader, ErrorState, ProgressBar, StatusBadge, EmptyState, Button } from "../../components/common";
import StageTracker from "../../components/cycle/StageTracker";
import RatingPill from "../../components/performance/RatingPill";
import { firstName, formatScore } from "../../utils/format";
import { GOAL_COLORS } from "../../utils/constants";
import { formatRatingScore } from "../../utils/ratingScale"; // [NEW] PM-13: score on the chosen scale

const api = { getEmployeeDashboard };

export default function EmployeeDashboard() {
  const { profile } = useRole();
  const { cycleId, cycle } = useCycle();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => api.getEmployeeDashboard(cycleId), [cycleId], { skip: !cycleId });

  if (loading || !data) return error ? <ErrorState message={error} onRetry={reload} /> : <Loader />;
  const s = data.scores;

  return (
    <div className="page">
      <PageHeader
        title={`Welcome back, ${firstName(profile.name)}`}
        subtitle={`${cycle?.name} performance cycle. Your goal sheet is ${data.sheetStatus.toLowerCase()}.`}
        actions={<Button icon={FiTarget} onClick={() => navigate("/my-goals")}>Open my goals</Button>}
      />

      <Card title="Where this cycle stands" subtitle="Each stage opens for a fixed window. Submit your part before it closes.">
        <StageTracker stages={data.stages} />
      </Card>

      <div className="stat-grid">
        <StatTile icon={FiTarget} label="Goals" value={data.goalCount} hint={`${data.goalWeightage}% goal percentage assigned`} onClick={() => navigate("/my-goals")} />
        <StatTile icon={FiCheckCircle} tone="success" label="Average goal progress" value={data.goalCompletion === null ? "—" : `${data.goalCompletion}%`} hint={`${data.completedGoals} of ${data.goalCount} goal${data.goalCount === 1 ? "" : "s"} fully completed`} />
        <StatTile icon={FiLayers} tone="info" label="KRA score" value={formatScore(s.kraScore)} hint="Out of 100" onClick={() => navigate("/my-kra-kpi")} />
        <StatTile
          icon={FiAward}
          tone="warning"
          label="Overall rating"
          value={s.finalScore === null ? "Not yet rated" : formatRatingScore(s.finalScore)}
          hint={s.rating ? s.rating : s.provisionalScore !== null ? `Provisional score ${formatScore(s.provisionalScore)}` : "Appears once HR finalizes"}
          onClick={() => navigate("/my-results")}
        />
      </div>

      <div className="grid-2-1">
        <Card
          title="Goal progress"
          subtitle="Manager-agreed achievement is used where available."
          actions={<Link to="/my-goals" className="link-btn">View all</Link>}
        >
          {data.goals.length === 0 ? (
            <EmptyState title="No goals yet" text="Add goals that total 100% weightage, then submit them to your manager." action={<Button onClick={() => navigate("/my-goals")}>Add goals</Button>} />
          ) : (
            <ul className="progress-list">
              {data.goals.map((g, i) => {
                const value = g.managerAchievement ?? g.achievement;
                return (
                  <li key={g.id}>
                    <div className="progress-list-head">
                      <span className="progress-list-name">
                        <span className="legend-swatch" style={{ background: GOAL_COLORS[i % GOAL_COLORS.length] }} />
                        {g.title}
                      </span>
                      <span className="progress-list-meta">{g.weightage}% <StatusBadge status={g.status} /></span>
                    </div>
                    <ProgressBar value={value} showValue tone={value >= 100 ? "success" : "primary"} label={g.title} />
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="stack">
          <Card title="Your next steps">
            {data.pendingActions.length === 0 ? (
              <EmptyState compact title="Nothing pending" text="You're up to date for now." />
            ) : (
              <ul className="action-list">
                {data.pendingActions.map((a) => (
                  <li key={a.title}>
                    <Link to={a.link} className={`action-item tone-${a.tone}`}>
                      <span>
                        <strong>{a.title}</strong>
                        <small>{a.detail}</small>
                      </span>
                      <FiArrowRight size={16} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Review status">
            <dl className="kv-list">
              <div><dt>Goal sheet</dt><dd><StatusBadge status={data.sheetStatus} /></dd></div>
              <div><dt>Self assessment</dt><dd><StatusBadge status={data.selfStatus} /></dd></div>
              <div><dt>Final result</dt><dd><StatusBadge status={data.resultStatus} /></dd></div>
              <div><dt>Rating</dt><dd><RatingPill rating={s.rating} tone={s.ratingTone} size="sm" /></dd></div>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}
